import React, { useCallback, useEffect } from 'react'
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js'

export type UseVideoPlayerProps = {
  options: VideoJsPlayerOptions
  autoPlay: boolean | string
  isReady?: boolean
}

export const useVideoPlayer = ({
  options,
  autoPlay,
  isReady = true,
}: UseVideoPlayerProps) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const playerRef = React.useRef<VideoJsPlayer | null>()

  const [isMute, setIsMute] = React.useState(true)
  const [isPaused, setIsPaused] = React.useState(true)

  useEffect(() => {
    const videoElement = videoRef.current
    if (isReady && videoElement) {
      if (playerRef.current) {
        console.log('既にplayerが存在するので、初期化をスキップします')
        playerRef.current.autoplay(autoPlay)
      } else {
        playerRef.current = videojs(videoElement, options, () => {
          videojs.log('player is ready')
        })
        console.log('playerを初期化しました')
        playerRef.current.autoplay(autoPlay)
      }
    }
  }, [autoPlay, isReady, options])

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose()
        playerRef.current = null
      }
    }
  }, [playerRef])

  const toggleMute = useCallback(() => {
    const player = playerRef.current
    if (player) {
      player.muted(!player.muted())
      setIsMute(player.muted())
    }
  }, [])

  const togglePause = useCallback(() => {
    const player = playerRef.current
    if (player) {
      player.pause()
      setIsPaused(player.paused())
    }
  }, [])

  const toggleDefaultPlayBackRate = useCallback(() => {
    const video = videoRef.current
    if (video) {
      video.playbackRate = 1
    }
  }, [])

  const toggleSecondPlayBackRate = useCallback(() => {
    const video = videoRef.current
    if (video) {
      video.playbackRate = 1.3
    }
  }, [])

  const toggleThirdPlayBackRate = useCallback(() => {
    const video = videoRef.current
    if (video) {
      video.playbackRate = 1.5
    }
  }, [])

  const togglePlay = useCallback(() => {
    const player = playerRef.current
    if (player) {
      player.play()
      setIsPaused(player.paused())
    }
  }, [])

  return {
    videoRef,
    playerRef,
    toggleMute,
    isMute,
    togglePause,
    isPaused,
    toggleDefaultPlayBackRate,
    toggleSecondPlayBackRate,
    toggleThirdPlayBackRate,
    togglePlay,
  }
}
